// @givmo/design-system — class-merge utility (shadcn convention).
// `cn` composes conditional class lists (clsx) and resolves Tailwind conflicts
// (tailwind-merge). Every component composes its variant classes through this.
//
// tailwind-merge is taught the design-system's CUSTOM scales so it classifies them into the right
// conflict group:
//   - font-size: the `text-sNN` tokens (text-s8 … text-s55). Without this, twMerge sees `text-s12`
//     as ambiguous and may treat it as conflicting with a text COLOR (`text-danger-700`), dropping
//     the color when the two land in different CVA groups. Registering them as font-sizes keeps
//     size + color independent.
//   - radius: the legacy `rounded-allNN` / `rounded-topNN` tokens, so conflicting radii collapse.
//   - shadow: the named `shadow-focus-glow` / `shadow-soft` tokens.
import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

const FONT_SIZE_TOKENS = [
  's8',
  's9',
  's10',
  's11',
  's12',
  's13',
  's14',
  's15',
  's16',
  's17',
  's18',
  's19',
  's20',
  's22',
  's24',
  's25',
  's28',
  's30',
  's32',
  's34',
  's36',
  's38',
  's40',
  's44',
  's48',
  's55',
];

const RADIUS_TOKENS = [
  'all4',
  'all6',
  'all7',
  'all10',
  'all12',
  'all15',
  'all17',
  'all20',
  'all40',
  'all45',
  'all50',
  'all100',
  'top10',
  'top26',
];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // Treat the custom `text-sNN` literals as font-sizes (not colors/leading) so they never
      // collide with `text-<color>` utilities.
      'font-size': [{ text: FONT_SIZE_TOKENS }],
      // Custom radii (legacy `rounded-allNN`/`rounded-topNN`).
      rounded: [{ rounded: RADIUS_TOKENS }],
      // Named box-shadows from the Flutter-ported layer.
      shadow: ['shadow-focus-glow', 'shadow-soft'],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
